import { useState } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";

import { createBench, updateBench } from "../../../../api/courtApis";
import type { BenchRow, HighCourtRow } from "./types";
import {
  FormField,
  InfoNotice,
  inputClassName,
  Modal,
  ModalActions,
  TextInput,
} from "./ModalShell";
import { useFormDirty } from "../../../../hooks/useFormDirty";

interface AddUpdateBencheProps {
  highCourt: HighCourtRow;
  bench?: BenchRow | null;
  onClose: () => void;
  onSaved: () => void;
}

const normalizeValue = (value?: string | number) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

export const AddUpdateBenche = ({
  highCourt,
  bench,
  onClose,
  onSaved,
}: AddUpdateBencheProps) => {
  const { data: benchResponse } = useSWR(
    highCourt?._id
      ? `/api/court/get-benches/${highCourt._id}?page=1&limit=1000`
      : null,
  );
  const existingBenches = ((benchResponse?.status === false
    ? []
    : benchResponse?.data) || []) as BenchRow[];

  const [form, setForm] = useState({
    benchName: bench?.benchName || "",
    location: bench?.location || "",
    status: bench?.status || "active",
  });

  const isDirty = useFormDirty(
    {
      benchName: bench?.benchName || "",
      location: bench?.location || "",
      status: bench?.status || "active",
    },
    form,
  );
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (bench && !isDirty) {
      toast.info("No changes detected");
      return;
    }
    if (!form.benchName || !form.location) {
      toast.error("Fill all required bench fields");
      return;
    }
    if (form.benchName.length > 100) {
      toast.error("Bench Name cannot exceed 100 characters");
      return;
    }

    if (form.location.length > 100) {
      toast.error("Location cannot exceed 100 characters");
      return;
    }
    if (/^\d+$/.test(form.benchName.trim())) {
      toast.error("Bench Name cannot contain only numeric values");
      return;
    }
    const duplicateBench = existingBenches.find((item) => {
      if (bench?._id && item._id === bench._id) return false;

      return (
        normalizeValue(item.benchName) === normalizeValue(form.benchName) ||
        normalizeValue(item.location) === normalizeValue(form.location)
      );
    });

    if (duplicateBench) {
      const duplicateField =
        normalizeValue(duplicateBench.benchName) ===
        normalizeValue(form.benchName)
          ? "Bench name"
          : "Location";

      toast.error(`${duplicateField} already exists for this high court`);
      return;
    }

    try {
      setIsSaving(true);
      const res = bench
        ? await updateBench({
            _id: bench._id,
            highcourt: bench.highcourt || highCourt._id,
            ...form,
          })
        : await createBench({ highCourtId: highCourt._id, ...form });

      if (res?.data?.status) {
        toast.success(res.data.message || "Bench saved");
        onSaved();
      } else {
        toast.error(res?.data?.message || "Failed to save bench");
      }
    } catch (error: any) {
      toast.error(error?.message || "Failed to save bench");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={bench ? "Edit Bench" : "Add New Bench"}
      subtitle={highCourt.highCourtName}
      onClose={onClose}
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <TextInput
          label="Bench Name"
          required
          value={form.benchName}
          onChange={(value) =>
            setForm({ ...form, benchName: value.slice(0, 100) })
          }
          placeholder="e.g. Lucknow Bench"
        />
        <TextInput
          label="Location"
          required
          value={form.location}
          onChange={(value) =>
            setForm({ ...form, location: value.slice(0, 100) })
          }
          placeholder="e.g. Lucknow"
        />
        <FormField label="Status" required>
          <select
            value={form.status}
            onChange={(event) =>
              setForm({ ...form, status: event.target.value })
            }
            className={inputClassName}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </FormField>
      </div>
      <InfoNotice tone="orange">
        After adding a bench, click on it to add court halls and assign judges
        to them.
      </InfoNotice>
      <ModalActions
        label={bench ? "Update Bench" : "Save Bench"}
        isSaving={isSaving}
        onCancel={onClose}
        onSave={handleSave}
        disabled={!!bench && !isDirty}
      />
    </Modal>
  );
};
